import React, { useState, useMemo } from 'react';
import { useSyncState } from '../src/hooks/useSyncState';
import ReportsScreen from './Reports';
import { GlobalUser, ProjectInfo } from '../src/types';

interface IntelligenceProps {
    projectInfo: ProjectInfo;
    currentUser?: GlobalUser;
    hasPermission: (module: string, action: 'view' | 'edit') => boolean;
}

export default function IntelligenceScreen({
    projectInfo,
    currentUser,
    hasPermission
}: IntelligenceProps) {
    const [activeSection, setActiveSection] = useState<
        'panel' | 'reports' | 'kpi' | 'predictive' | 'recommendations' | 'history' | 'simulations' | 'config'
    >('panel');

    const activeProjectId = typeof window !== 'undefined' ? (localStorage.getItem('selectedProjectId') || 'data') : 'data';
    const collectionName = activeProjectId !== 'data' ? `project_${activeProjectId}` : 'data';

    // 1. Load sync states for live analytics
    const [localProjectInfo] = useSyncState<any>(collectionName, 'projectInfo', projectInfo);
    const [localTasks] = useSyncState<any[]>(collectionName, 'tasks', []);
    const [localWeeklyPlans] = useSyncState<any[]>(collectionName, 'weeklyPlans', []);
    const [localSavedProtocols] = useSyncState<Record<string, any>>(collectionName, 'savedProtocols', {});
    const [localContracts] = useSyncState<any[]>(collectionName, 'contracts', []);
    const [localResponsibles] = useSyncState<any[]>(collectionName, 'responsibles', []);
    const [lookaheadPlans] = useSyncState<any[]>(collectionName, 'lookaheadPlans', []);
    const [projects] = useSyncState<any[]>('data', 'projects', []);

    const activeProject = projects.find(p => String(p.id) === String(activeProjectId));
    const resolvedProjectInfo = activeProject ? {
        name: activeProject.name,
        startDate: activeProject.startDate || activeProject.start || projectInfo.startDate,
        endDate: activeProject.endDate || activeProject.end || projectInfo.endDate
    } : localProjectInfo;

    // Load CNC Master lists safely
    const [globalCNCGroups] = useSyncState<any[]>('data', 'globalCNCGroups', []);
    const [cncList] = useSyncState<any[]>('data', 'cnc', []);

    // Load Settings state from localStorage to make configuration real and persistent
    const [settings, setSettings] = useState(() => {
        try {
            const saved = typeof window !== 'undefined' ? localStorage.getItem(`intel_settings_${activeProjectId}`) : null;
            if (saved) return JSON.parse(saved);
        } catch (e) {}
        return {
            ganttAlertThreshold: 5,
            excludeMilestones: true,
            lookaheadWindowWeeks: 3,
            targetPpc: 80,
            qualityTolerance: 90,
            protocolExpirationDays: 7,
            simulationModel: 'cpm',
            recalcFrequency: 'sync',
            predictorSensitivity: 70
        };
    });

    // Temp form state
    const [formSettings, setFormSettings] = useState({ ...settings });
    const [showSavedToast, setShowSavedToast] = useState(false);

    const handleSaveConfig = () => {
        setSettings({ ...formSettings });
        if (typeof window !== 'undefined') {
            localStorage.setItem(`intel_settings_${activeProjectId}`, JSON.stringify(formSettings));
        }
        setShowSavedToast(true);
        setTimeout(() => setShowSavedToast(false), 3000);
    };

    // Merge CNC lists to map codes to names
    const allCNCs = useMemo(() => {
        const unique = new Map<string, string>();
        const list = [...globalCNCGroups, ...cncList];
        list.forEach(g => {
            if (g && Array.isArray(g.causes)) {
                g.causes.forEach((c: any) => {
                    unique.set(c.id, c.name);
                });
            }
        });
        return unique;
    }, [globalCNCGroups, cncList]);

    // Flatten lookahead constraints
    const allConstraints = useMemo(() => {
        if (!Array.isArray(lookaheadPlans)) return [];
        return lookaheadPlans.flatMap(p => {
            if (p && Array.isArray(p.constraints)) {
                return p.constraints.map(c => ({
                    ...c,
                    planName: p.name || 'Lookahead'
                }));
            }
            return [];
        });
    }, [lookaheadPlans]);

    // 2. Compute dynamic KPIs based on live operational data and user-defined settings
    const metrics = useMemo(() => {
        const leafTasks = localTasks.filter(t => t.type === 'task');
        const totalTasks = leafTasks.length;
        
        // Count delayed tasks considering settings.ganttAlertThreshold & excludeMilestones
        const delayedTasks = leafTasks.filter(t => {
            if (settings.excludeMilestones && (t.duration === 0 || t.name?.toLowerCase().includes('hito') || t.name?.toLowerCase().includes('milestone'))) {
                return false;
            }
            if (t.end && new Date(t.end) < new Date() && (t.progress || 0) < 100) {
                const diffTime = Math.abs(new Date().getTime() - new Date(t.end).getTime());
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return diffDays >= settings.ganttAlertThreshold;
            }
            return false;
        }).length;

        // Calculate PPC
        let totalComprometido = 0;
        let totalEjecutado = 0;
        localWeeklyPlans.forEach(plan => {
            if (plan && Array.isArray(plan.allocations)) {
                plan.allocations.forEach(alloc => {
                    if (alloc.isWeeklySubtask) return;
                    totalComprometido++;
                    if (alloc.executed) {
                        totalEjecutado++;
                    }
                });
            }
        });
        const ppc = totalComprometido > 0 ? (totalEjecutado / totalComprometido) * 100 : 0;

        // Quality
        const protocolsList = Object.values(localSavedProtocols);
        const totalProtocols = protocolsList.length;
        const pendingProtocols = protocolsList.filter((p: any) => p.status === 'Abierto' || p.status === 'Pendiente').length;
        const approvalRate = totalProtocols > 0 
            ? ((totalProtocols - pendingProtocols) / totalProtocols) * 100 
            : 100;

        // Subcontracts
        const activeSubcontracts = localContracts.filter(c => c.status === 'Active' || !c.status).length;
        const totalApprovedAmount = localContracts.reduce((acc, curr) => acc + (Number(curr.approvedAmount) || 0), 0);

        return {
            totalTasks,
            delayedTasks,
            ppc,
            totalComprometido,
            totalEjecutado,
            totalProtocols,
            pendingProtocols,
            approvalRate,
            activeSubcontracts,
            totalApprovedAmount
        };
    }, [localTasks, localWeeklyPlans, localSavedProtocols, localContracts, settings]);

    // 3. ANALISIS PREDICTIVO: Dynamic analysis of actual database causes
    const predictiveData = useMemo(() => {
        // Count root causes of non-compliance (CNC) from weekly allocations
        const cncCounts: Record<string, number> = {};
        localWeeklyPlans.forEach(plan => {
            if (plan && Array.isArray(plan.allocations)) {
                plan.allocations.forEach(alloc => {
                    if (!alloc.executed && alloc.cncId) {
                        cncCounts[alloc.cncId] = (cncCounts[alloc.cncId] || 0) + 1;
                    }
                });
            }
        });

        const sortedCNC = Object.entries(cncCounts)
            .map(([id, val]) => {
                const name = allCNCs.get(id) || `Causa ${id}`;
                return { id, name, count: val };
            })
            .sort((a, b) => b.count - a.count);

        // Analyze delays by responsible
        const delayByResponsible: Record<string, number> = {};
        const leafTasks = localTasks.filter(t => t.type === 'task');
        leafTasks.forEach(t => {
            const isLate = t.end && new Date(t.end) < new Date() && (t.progress || 0) < 100;
            if (isLate && t.responsibleId) {
                delayByResponsible[t.responsibleId] = (delayByResponsible[t.responsibleId] || 0) + 1;
            }
        });

        const sortedDelaysByResponsible = Object.entries(delayByResponsible)
            .map(([respId, count]) => {
                const respObj = localResponsibles.find(r => r.id === respId);
                return {
                    name: respObj?.name || `Resp. ${respId}`,
                    count
                };
            })
            .sort((a, b) => b.count - a.count);

        return {
            sortedCNC,
            sortedDelaysByResponsible
        };
    }, [localWeeklyPlans, localTasks, localResponsibles, allCNCs]);

    // 4. RECOMENDACIONES: Live rule-based actions recommended based on active project state and config limits
    const recommendations = useMemo(() => {
        const recs: { type: 'alert' | 'success' | 'info'; title: string; desc: string; module: string }[] = [];

        // Rule A: Carta Gantt Delays
        if (metrics.delayedTasks > 0) {
            recs.push({
                type: 'alert',
                module: 'Planificación Maestra',
                title: 'Desviaciones Críticas en Carta Gantt',
                desc: `Se identificaron ${metrics.delayedTasks} tareas fuera de plazo (retraso >= ${settings.ganttAlertThreshold} días). Se recomienda revisar holguras y dependencias.`
            });
        }

        // Rule B: Lookahead Window Constraints
        // Filter constraints within settings.lookaheadWindowWeeks
        const pendingConstraints = allConstraints.filter(c => {
            if (c.status === 'Liberado') return false;
            if (c.limitDate) {
                const limit = new Date(c.limitDate);
                const maxDate = new Date();
                maxDate.setDate(maxDate.getDate() + (settings.lookaheadWindowWeeks * 7));
                return limit <= maxDate;
            }
            return true;
        });

        if (pendingConstraints.length > 0) {
            const responsibleIds = Array.from(new Set(pendingConstraints.map(c => c.responsibleId).filter(Boolean)));
            const names = responsibleIds.map(id => localResponsibles.find(r => r.id === id)?.name || id);
            recs.push({
                type: 'alert',
                module: 'Planificación Intermedia',
                title: `Restricciones del Lookahead en la ventana (${settings.lookaheadWindowWeeks} semanas)`,
                desc: `Hay ${pendingConstraints.length} restricciones sin liberar con vencimiento cercano. Contactar urgentemente a: ${names.join(', ')}.`
            });
        }

        // Rule C: PPC Performance vs Target Configured
        if (metrics.totalComprometido > 0) {
            if (metrics.ppc < settings.targetPpc) {
                const topCnc = predictiveData.sortedCNC[0]?.name || 'Factores no especificados';
                recs.push({
                    type: 'info',
                    module: 'Planificación Semanal',
                    title: `Desviación de PPC (Meta: ${settings.targetPpc}%)`,
                    desc: `El PPC acumulado es de ${metrics.ppc.toFixed(1)}%, por debajo de tu meta. La causa recurrente identificada en base de datos es "${topCnc}".`
                });
            } else {
                recs.push({
                    type: 'success',
                    module: 'Planificación Semanal',
                    title: 'Excelente Cumplimiento de PPC',
                    desc: `El PPC se encuentra en un saludable ${metrics.ppc.toFixed(1)}% (superando la meta del ${settings.targetPpc}%).`
                });
            }
        }

        // Rule D: Quality Checklists vs Target Configured
        if (metrics.totalProtocols > 0) {
            if (metrics.approvalRate < settings.qualityTolerance) {
                recs.push({
                    type: 'alert',
                    module: 'Control de Calidad',
                    title: `Optimización de Calidad (Meta: ${settings.qualityTolerance}%)`,
                    desc: `La tasa de aprobación actual es del ${metrics.approvalRate.toFixed(1)}% con ${metrics.pendingProtocols} pendientes. Evitar cuellos de botella en la entrega.`
                });
            } else {
                recs.push({
                    type: 'success',
                    module: 'Control de Calidad',
                    title: 'Calidad Operativa al Día',
                    desc: `La tasa de calidad es del ${metrics.approvalRate.toFixed(1)}% (cumpliendo la tolerancia de aprobación).`
                });
            }
        }

        return recs;
    }, [metrics, allConstraints, localResponsibles, predictiveData, settings]);

    // 5. SIMULADOR DE ESCENARIOS: Interactive CPM delay impact simulator
    const [selectedSimTaskId, setSelectedSimTaskId] = useState('');
    const [simDelayDays, setSimDelayDays] = useState(10);
    const [simResult, setSimResult] = useState<{
        originalProjectEnd: string;
        simulatedProjectEnd: string;
        diffDays: number;
        affectedCount: number;
    } | null>(null);

    const handleRunSimulation = () => {
        if (!selectedSimTaskId) return;

        const clonedTasks = JSON.parse(JSON.stringify(localTasks));
        const taskMap = new Map<string, any>();
        clonedTasks.forEach((t: any) => taskMap.set(t.id, t));

        const startTask = taskMap.get(selectedSimTaskId);
        if (!startTask) return;

        const queue: string[] = [selectedSimTaskId];
        const shifted = new Set<string>();

        if (startTask.end) {
            const endDate = new Date(startTask.end);
            endDate.setDate(endDate.getDate() + simDelayDays);
            startTask.end = endDate.toISOString().split('T')[0];
            shifted.add(selectedSimTaskId);
        }

        // Propagate delay through predecessors chain
        while (queue.length > 0) {
            const currentId = queue.shift()!;
            clonedTasks.forEach((t: any) => {
                if (t.predecessors && t.predecessors.includes(currentId) && !shifted.has(t.id)) {
                    if (t.start && t.end) {
                        const originalStart = new Date(t.start);
                        const originalEnd = new Date(t.end);
                        
                        originalStart.setDate(originalStart.getDate() + simDelayDays);
                        originalEnd.setDate(originalEnd.getDate() + simDelayDays);
                        
                        t.start = originalStart.toISOString().split('T')[0];
                        t.end = originalEnd.toISOString().split('T')[0];
                        
                        shifted.add(t.id);
                        queue.push(t.id);
                    }
                }
            });
        }

        const originalProjectEnd = localTasks.reduce((max, t) => {
            if (!t.end) return max;
            const d = new Date(t.end);
            return d > max ? d : max;
        }, new Date(0));

        const simulatedProjectEnd = clonedTasks.reduce((max: Date, t: any) => {
            if (!t.end) return max;
            const d = new Date(t.end);
            return d > max ? d : max;
        }, new Date(0));

        setSimResult({
            originalProjectEnd: originalProjectEnd.toISOString().split('T')[0],
            simulatedProjectEnd: simulatedProjectEnd.toISOString().split('T')[0],
            diffDays: Math.round((simulatedProjectEnd.getTime() - originalProjectEnd.getTime()) / (1000 * 60 * 60 * 24)),
            affectedCount: shifted.size
        });
    };

    // 6. COMPARADOR HISTORICO: Compare current project vs selected project
    const [compareProjectId, setCompareProjectId] = useState('');
    const compareCollectionName = compareProjectId ? `project_${compareProjectId}` : 'data';
    const [compareProjectInfo] = useSyncState<any>(compareCollectionName, 'projectInfo', null);
    const [compareTasks] = useSyncState<any[]>(compareCollectionName, 'tasks', []);
    const [compareWeeklyPlans] = useSyncState<any[]>(compareCollectionName, 'weeklyPlans', []);
    const [compareSavedProtocols] = useSyncState<Record<string, any>>(compareCollectionName, 'savedProtocols', {});

    const compareMetrics = useMemo(() => {
        if (!compareProjectId) return null;

        const leafTasks = compareTasks.filter(t => t.type === 'task');
        const totalTasks = leafTasks.length;
        const delayedTasks = leafTasks.filter(t => {
            const isLate = t.end && new Date(t.end) < new Date() && (t.progress || 0) < 100;
            return isLate;
        }).length;

        let totalComprometido = 0;
        let totalEjecutado = 0;
        compareWeeklyPlans.forEach(plan => {
            if (plan && Array.isArray(plan.allocations)) {
                plan.allocations.forEach(alloc => {
                    if (alloc.isWeeklySubtask) return;
                    totalComprometido++;
                    if (alloc.executed) {
                        totalEjecutado++;
                    }
                });
            }
        });
        const ppc = totalComprometido > 0 ? (totalEjecutado / totalComprometido) * 100 : 0;

        const protocolsList = Object.values(compareSavedProtocols);
        const totalProtocols = protocolsList.length;
        const pendingProtocols = protocolsList.filter((p: any) => p.status === 'Abierto' || p.status === 'Pendiente').length;
        const approvalRate = totalProtocols > 0 
            ? ((totalProtocols - pendingProtocols) / totalProtocols) * 100 
            : 100;

        return {
            name: compareProjectInfo?.name || 'Obra Comparada',
            totalTasks,
            delayedTasks,
            ppc,
            totalProtocols,
            approvalRate
        };
    }, [compareProjectId, compareProjectInfo, compareTasks, compareWeeklyPlans, compareSavedProtocols]);

    const sections = [
        { id: 'panel', label: 'Panel Ejecutivo', icon: 'dashboard', desc: 'Resumen estratégico de la obra' },
        { id: 'reports', label: 'Reportes y Filtros', icon: 'bar_chart', desc: 'Diseñador y catalogador de reportes' },
        { id: 'kpi', label: 'Indicadores KPI', icon: 'speed', desc: 'Arquitectura de métricas clave' },
        { id: 'predictive', label: 'Análisis Predictivo', icon: 'query_stats', desc: 'Motor de causas raíz reales' },
        { id: 'recommendations', label: 'Recomendaciones', icon: 'insights', desc: 'Acciones sugeridas por el sistema' },
        { id: 'history', label: 'Comparador Histórico', icon: 'compare_arrows', desc: 'Análisis comparativo de proyectos' },
        { id: 'simulations', label: 'Simulaciones', icon: 'science', desc: 'Análisis de impacto y escenarios' },
        { id: 'config', label: 'Configuración', icon: 'settings', desc: 'Parámetros del motor analítico' }
    ] as const;

    return (
        <div className="flex h-screen w-full overflow-hidden bg-slate-50 dark:bg-[#0b0f19] font-display">
            {/* Left sidebar sub-navigator */}
            {activeSection !== 'reports' && (
                <aside className="hidden lg:flex h-full w-72 flex-col border-r border-[#e7ecf3] dark:border-slate-800/80 bg-white dark:bg-[#0f1524] flex-shrink-0 animate-fade-in animate-duration-200">
                    <div className="flex h-full flex-col justify-between p-5">
                        <div className="flex flex-col gap-6">
                            <div className="flex items-center gap-3 px-1.5">
                                <div className="size-10 rounded-xl bg-gradient-to-tr from-indigo-650 to-cyan-500 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20">
                                    <span className="material-symbols-outlined text-[24px]">psychology</span>
                                </div>
                                <div className="flex flex-col truncate">
                                    <h1 className="text-sm font-black tracking-tight text-slate-850 dark:text-white leading-snug">Inteligencia Operativa</h1>
                                    <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">ConstructOS Core</p>
                                </div>
                            </div>
                            <nav className="flex flex-col gap-1.5">
                                {sections.map((sec) => (
                                    <button
                                        key={sec.id}
                                        onClick={() => setActiveSection(sec.id)}
                                        className={`flex flex-col items-start gap-0.5 px-3.5 py-3 rounded-2xl transition-all border text-left ${
                                            activeSection === sec.id
                                                ? 'bg-gradient-to-r from-indigo-50/70 to-indigo-100/30 dark:from-indigo-950/40 dark:to-cyan-950/20 border-indigo-200 dark:border-indigo-850 text-slate-850 dark:text-white shadow-sm font-bold scale-[1.02]'
                                                : 'border-transparent text-slate-550 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900/60'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className={`material-symbols-outlined text-[20px] ${activeSection === sec.id ? 'text-primary animate-pulse' : 'text-slate-400'}`}>
                                                {sec.icon}
                                            </span>
                                            <span className="text-xs font-black tracking-wide uppercase">{sec.label}</span>
                                        </div>
                                        <span className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 line-clamp-1">{sec.desc}</span>
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>
                </aside>
            )}

            {/* Main Content Pane */}
            <div className="flex-1 flex flex-col min-w-0 bg-[#fafbfe] dark:bg-[#0b0f19] relative overflow-y-auto">
                {activeSection !== 'reports' && (
                    <header className="flex items-center justify-between px-6 py-4 bg-white dark:bg-[#0f1524] border-b border-slate-200 dark:border-slate-800/80 shrink-0">
                        <div className="flex items-center gap-3">
                            <span className="lg:hidden material-symbols-outlined text-primary text-[28px]">psychology</span>
                            <div>
                                <h2 className="text-base font-extrabold text-slate-850 dark:text-white">
                                    {sections.find(s => s.id === activeSection)?.label}
                                </h2>
                                <p className="text-[10px] text-slate-450 dark:text-slate-500 mt-0.5">
                                    Obra Activa: <strong className="text-indigo-600 dark:text-indigo-400 font-bold">{resolvedProjectInfo?.name || 'Cargando...'}</strong>
                                </p>
                            </div>
                        </div>

                        {/* Mobile Switcher */}
                        <div className="lg:hidden">
                            <select
                                value={activeSection}
                                onChange={(e) => setActiveSection(e.target.value as any)}
                                className="px-3 py-1.5 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-800 dark:text-white"
                            >
                                {sections.map(sec => (
                                    <option key={sec.id} value={sec.id}>{sec.label}</option>
                                ))}
                            </select>
                        </div>
                    </header>
                )}

                {/* Floating Saved Toast Notification */}
                {showSavedToast && (
                    <div className="fixed top-6 right-6 z-55 flex items-center gap-2 bg-emerald-500 text-white px-4 py-3 rounded-2xl shadow-xl animate-bounce text-xs font-bold transition-all">
                        <span className="material-symbols-outlined">check_circle</span>
                        Configuración guardada y recalculada con éxito
                    </div>
                )}

                <div className="flex-1 min-h-0 flex flex-col">
                    
                    {/* PANEL EJECUTIVO */}
                    {activeSection === 'panel' && (
                        <div className="p-6 md:p-8 space-y-6 max-w-6xl w-full mx-auto animate-fade-in">
                            <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-slate-950 p-6 md:p-8 rounded-3xl border border-indigo-900/40 text-white shadow-xl space-y-4">
                                <div className="flex items-center gap-3">
                                    <span className="px-2.5 py-0.5 bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[9px] font-black tracking-widest rounded-full uppercase animate-pulse">Inteligencia de Datos</span>
                                </div>
                                <h3 className="text-xl md:text-2xl font-black">Centro de Inteligencia Operacional</h3>
                                <p className="text-slate-300 text-xs md:text-sm leading-relaxed max-w-3xl">
                                    Esta sección unifica los datos operativos de **K-LyDApp** cargados en la base de datos de tu obra. A continuación, el sistema evalúa la planificación semanal, control de calidad y plazos de la carta Gantt para darte una visión del estado de tu obra.
                                </p>
                            </div>

                            {/* Tarjetas Metodológicas Generadas con Datos Reales */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                <div className="bg-white dark:bg-[#0f1524] p-5 rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm space-y-3">
                                    <div className="size-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-[22px]">calendar_month</span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Planificación Maestra</span>
                                        <h4 className="text-xl font-black text-slate-850 dark:text-white mt-1">{metrics.totalTasks} tareas</h4>
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            {metrics.delayedTasks > 0 
                                                ? `⚠️ ${metrics.delayedTasks} tareas fuera de plazo.`
                                                : '✅ Carta Gantt al día.'}
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-[#0f1524] p-5 rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm space-y-3">
                                    <div className="size-10 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-[22px]">assignment_turned_in</span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Cumplimiento PPC</span>
                                        <h4 className="text-xl font-black text-slate-850 dark:text-white mt-1">{metrics.ppc.toFixed(1)}%</h4>
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            {metrics.totalComprometido} compromisos asignados.
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-[#0f1524] p-5 rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm space-y-3">
                                    <div className="size-10 rounded-xl bg-purple-500/10 text-purple-500 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-[22px]">fact_check</span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Control de Calidad</span>
                                        <h4 className="text-xl font-black text-slate-850 dark:text-white mt-1">{metrics.approvalRate.toFixed(1)}%</h4>
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            {metrics.pendingProtocols} de {metrics.totalProtocols} en revisión.
                                        </p>
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-[#0f1524] p-5 rounded-2xl border border-slate-200 dark:border-slate-800/80 shadow-sm space-y-3">
                                    <div className="size-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-[22px]">handshake</span>
                                    </div>
                                    <div>
                                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Especialidades</span>
                                        <h4 className="text-xl font-black text-slate-850 dark:text-white mt-1">{metrics.activeSubcontracts}</h4>
                                        <p className="text-[11px] text-slate-500 mt-1">
                                            Subcontratos vigentes en obra.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Alertas y Shortcut a Reportes */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2 bg-white dark:bg-[#0f1524] p-6 rounded-3xl border border-slate-200 dark:border-slate-800/80 shadow-sm space-y-4">
                                    <h4 className="text-xs font-black text-slate-850 dark:text-white uppercase tracking-wider">Hallazgos Clave de Gestión</h4>
                                    
                                    <div className="divide-y divide-slate-100 dark:divide-slate-800/60 text-xs space-y-2">
                                        {recommendations.slice(0, 3).map((rec, idx) => (
                                            <div key={idx} className="py-3 flex items-start gap-3">
                                                <span className={`material-symbols-outlined mt-0.5 ${
                                                    rec.type === 'alert' ? 'text-amber-550 mt-0.5 animate-bounce' : rec.type === 'success' ? 'text-emerald-500' : 'text-indigo-500'
                                                }`}>
                                                    {rec.type === 'alert' ? 'warning' : rec.type === 'success' ? 'check_circle' : 'info'}
                                                </span>
                                                <div>
                                                    <p className="font-bold text-slate-800 dark:text-slate-200">{rec.title} ({rec.module})</p>
                                                    <p className="text-slate-500 mt-0.5 leading-relaxed">{rec.desc}</p>
                                                </div>
                                            </div>
                                        ))}
                                        {recommendations.length === 0 && (
                                            <p className="text-slate-450 dark:text-slate-600 text-center py-4 italic">No hay alertas detectadas para el proyecto actual.</p>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-[#0f1524] p-6 rounded-3xl border border-slate-200 dark:border-slate-800/80 shadow-sm flex flex-col justify-between">
                                    <div className="space-y-3">
                                        <h4 className="text-xs font-black text-slate-850 dark:text-white uppercase tracking-wider">Acceso a Reportes y Diseñador</h4>
                                        <p className="text-xs text-slate-500 leading-relaxed">
                                            Reutiliza el catálogo de reportes completo o diseña nuevos cuadros de mando estratégicos directamente en esta sección.
                                        </p>
                                    </div>
                                    <button 
                                        onClick={() => setActiveSection('reports')}
                                        className="w-full mt-6 py-3 bg-gradient-to-r from-indigo-650 to-indigo-600 hover:from-indigo-600 hover:to-indigo-550 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center justify-center gap-1.5"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">bar_chart</span>
                                        Ir al Panel de Reportes
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* REPORTES (Reutilizando el módulo existente) */}
                    {activeSection === 'reports' && (
                        <div className="flex-1 flex flex-col min-h-0 bg-[#fafbfe] dark:bg-[#0b0f19]">
                            <div className="px-5 py-3.5 bg-white dark:bg-[#0f1524] border-b border-slate-200 dark:border-slate-800/80 shrink-0 flex items-center justify-between no-print">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setActiveSection('panel')}
                                        className="flex items-center justify-center size-8 rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-indigo-500 transition-all"
                                        title="Volver al Centro de Inteligencia"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">arrow_back</span>
                                    </button>
                                    <div>
                                        <span className="px-2 py-0.5 bg-indigo-50/60 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-850 text-[9px] font-black text-indigo-600 dark:text-indigo-400 rounded-full uppercase tracking-wider">Herramienta</span>
                                        <h3 className="text-xs font-extrabold text-slate-800 dark:text-white mt-0.5">Visor de Reportes y Datos</h3>
                                    </div>
                                </div>
                                <span className="text-[10px] text-slate-450 italic">Centro de Inteligencia K-LyDApp</span>
                            </div>
                            
                            <div className="flex-1 min-h-0">
                                <ReportsScreen
                                    projectInfo={resolvedProjectInfo}
                                    currentUser={currentUser}
                                    hasPermission={hasPermission}
                                />
                            </div>
                        </div>
                    )}

                    {/* INDICADORES KPI */}
                    {activeSection === 'kpi' && (
                        <div className="p-6 md:p-8 space-y-6 max-w-4xl w-full mx-auto animate-fade-in text-center py-20">
                            <span className="material-symbols-outlined text-slate-400 text-6xl">speed</span>
                            <h3 className="text-xl font-bold text-slate-850 dark:text-white mt-4">Indicadores KPI Estratégicos</h3>
                            <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed mt-2">
                                Panel reservado para la consolidación de indicadores de alto nivel como PPC acumulados, curvas S de avance contractual y ratios de desviaciones del proyecto.
                            </p>
                            <div className="border border-dashed border-slate-200 dark:border-slate-850 p-6 rounded-2xl mt-8 max-w-sm mx-auto bg-slate-50/50 dark:bg-slate-900/10">
                                <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-widest block mb-1">Estatus del Módulo</span>
                                <span className="text-xs text-slate-550 dark:text-slate-450">Arquitectura de KPIs configurada y lista para inyección de métricas operativas directas.</span>
                            </div>
                        </div>
                    )}

                    {/* ANALISIS PREDICTIVO */}
                    {activeSection === 'predictive' && (
                        <div className="p-6 md:p-8 space-y-6 max-w-4xl w-full mx-auto animate-fade-in">
                            <div className="space-y-6">
                                <div className="bg-white dark:bg-[#0f1524] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-white flex items-center gap-2">
                                        <span className="material-symbols-outlined text-indigo-500">pie_chart</span>
                                        Análisis Histórico de Causas de No Cumplimiento (CNC)
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Distribución de las causas reales que impidieron el cumplimiento de los compromisos semanales programados en el proyecto.
                                    </p>

                                    <div className="space-y-3.5 pt-2">
                                        {predictiveData.sortedCNC.map((item, idx) => (
                                            <div key={idx} className="space-y-1">
                                                <div className="flex justify-between text-xs font-semibold">
                                                    <span className="text-slate-700 dark:text-slate-350">{item.name} <strong className="text-[10px] text-slate-400">({item.id})</strong></span>
                                                    <span className="text-slate-850 dark:text-white">{item.count} ocurrencias</span>
                                                </div>
                                                <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
                                                    <div 
                                                        className="bg-indigo-650 dark:bg-indigo-500 h-full rounded-full transition-all"
                                                        style={{ width: `${Math.min(100, (item.count / Math.max(1, localWeeklyPlans.length)) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        ))}
                                        {predictiveData.sortedCNC.length === 0 && (
                                            <p className="text-xs text-slate-400 dark:text-slate-650 py-6 text-center italic">No hay fallas de compromisos registradas con causas (CNC) en la base de datos de esta obra.</p>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white dark:bg-[#0f1524] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                                    <h3 className="text-sm font-bold text-slate-850 dark:text-white flex items-center gap-2">
                                        <span className="material-symbols-outlined text-amber-500">warning</span>
                                        Desviaciones por Responsable (Tareas de Carta Gantt Fuera de Plazo)
                                    </h3>
                                    <p className="text-xs text-slate-500">
                                        Cantidad de tareas de la Carta Gantt retrasadas (retardo mayor al umbral de {settings.ganttAlertThreshold} días configurado).
                                    </p>

                                    <div className="space-y-3.5 pt-2">
                                        {predictiveData.sortedDelaysByResponsible.map((item, idx) => (
                                            <div key={idx} className="space-y-1">
                                                <div className="flex justify-between text-xs font-semibold">
                                                    <span className="text-slate-700 dark:text-slate-350">{item.name}</span>
                                                    <span className="text-slate-850 dark:text-white">{item.count} tareas atrasadas</span>
                                                </div>
                                                <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
                                                    <div 
                                                        className="bg-amber-500 h-full rounded-full transition-all"
                                                        style={{ width: `${Math.min(100, (item.count / Math.max(1, localTasks.length)) * 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        ))}
                                        {predictiveData.sortedDelaysByResponsible.length === 0 && (
                                            <p className="text-xs text-slate-450 dark:text-slate-600 py-6 text-center italic">No se registran tareas retrasadas con responsable asignado.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* RECOMENDACIONES */}
                    {activeSection === 'recommendations' && (
                        <div className="p-6 md:p-8 space-y-6 max-w-4xl w-full mx-auto animate-fade-in">
                            <div className="bg-white dark:bg-[#0f1524] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                                <h3 className="text-sm font-bold text-slate-850 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-indigo-500">tips_and_updates</span>
                                    Acciones de Mitigación Generadas
                                </h3>
                                <p className="text-xs text-slate-550 leading-relaxed">
                                    A continuación se listan las recomendaciones analíticas construidas a partir de las desviaciones reales detectadas en tu proyecto activo en base a tus umbrales (PPC, restricciones y Carta Gantt).
                                </p>

                                <div className="space-y-4 pt-2">
                                    {recommendations.map((rec, idx) => (
                                        <div key={idx} className="p-4 rounded-xl border border-slate-150 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-900/30 flex items-start gap-4">
                                            <div className={`p-2.5 rounded-lg shrink-0 ${
                                                rec.type === 'alert' ? 'bg-amber-500/10 text-amber-500' : rec.type === 'success' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-indigo-500/10 text-indigo-500'
                                            }`}>
                                                <span className="material-symbols-outlined text-[20px] block">
                                                    {rec.type === 'alert' ? 'report' : rec.type === 'success' ? 'verified' : 'lightbulb'}
                                                </span>
                                            </div>
                                            <div className="space-y-1 text-xs">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-extrabold text-slate-800 dark:text-slate-200">{rec.title}</span>
                                                    <span className="px-2 py-0.5 bg-slate-200/50 dark:bg-slate-850 text-slate-500 dark:text-slate-400 text-[8px] font-black tracking-widest rounded-full uppercase">{rec.module}</span>
                                                </div>
                                                <p className="text-slate-500 dark:text-slate-450 leading-relaxed">{rec.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                    {recommendations.length === 0 && (
                                        <div className="text-center py-10 space-y-2">
                                            <span className="material-symbols-outlined text-slate-350 text-4xl">emoji_emotions</span>
                                            <p className="text-xs text-slate-400 italic">No hay acciones correctivas necesarias. Tu proyecto no presenta desviaciones críticas.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* COMPARADOR HISTORICO */}
                    {activeSection === 'history' && (
                        <div className="p-6 md:p-8 space-y-6 max-w-4xl w-full mx-auto animate-fade-in">
                            <div className="bg-white dark:bg-[#0f1524] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                                <h3 className="text-sm font-bold text-slate-850 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-indigo-500">compare</span>
                                    Comparar Obra Activa con otro Proyecto en la Base de Datos
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Selecciona un proyecto disponible en K-LyDapp para realizar un análisis comparativo directo de plazos, PPC y calidad.
                                </p>

                                <div className="flex gap-4 pt-2">
                                    <select
                                        value={compareProjectId}
                                        onChange={(e) => setCompareProjectId(e.target.value)}
                                        className="flex-1 p-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl text-xs font-bold text-slate-800 dark:text-white"
                                    >
                                        <option value="">-- Seleccionar proyecto para comparar --</option>
                                        {projects.filter(p => p.id !== activeProjectId).map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {compareMetrics ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 text-xs">
                                        {/* Current Project */}
                                        <div className="bg-slate-50/50 dark:bg-slate-900/30 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-4">
                                            <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Proyecto A (Activo)</span>
                                            <h4 className="font-extrabold text-slate-800 dark:text-white text-sm">{resolvedProjectInfo?.name || 'Obra Activa'}</h4>
                                            
                                            <div className="space-y-3 pt-2">
                                                <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                                    <span className="text-slate-500">Cumplimiento PPC Semanal:</span>
                                                    <strong className="text-slate-800 dark:text-white">{metrics.ppc.toFixed(1)}%</strong>
                                                </div>
                                                <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                                    <span className="text-slate-500">Tareas en Gantt / Atrasadas:</span>
                                                    <strong className="text-slate-800 dark:text-white">{metrics.totalTasks} / {metrics.delayedTasks}</strong>
                                                </div>
                                                <div className="flex justify-between pb-1.5">
                                                    <span className="text-slate-500">Aprobación de Calidad:</span>
                                                    <strong className="text-slate-800 dark:text-white">{metrics.approvalRate.toFixed(1)}%</strong>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Compared Project */}
                                        <div className="bg-slate-50/50 dark:bg-slate-900/30 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 space-y-4">
                                            <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Proyecto B (Comparado)</span>
                                            <h4 className="font-extrabold text-slate-800 dark:text-white text-sm">{compareMetrics.name}</h4>
                                            
                                            <div className="space-y-3 pt-2">
                                                <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                                    <span className="text-slate-500">Cumplimiento PPC Semanal:</span>
                                                    <strong className="text-slate-800 dark:text-white">{compareMetrics.ppc.toFixed(1)}%</strong>
                                                </div>
                                                <div className="flex justify-between border-b border-slate-100 dark:border-slate-800 pb-1.5">
                                                    <span className="text-slate-500">Tareas en Gantt / Atrasadas:</span>
                                                    <strong className="text-slate-800 dark:text-white">{compareMetrics.totalTasks} / {compareMetrics.delayedTasks}</strong>
                                                </div>
                                                <div className="flex justify-between pb-1.5">
                                                    <span className="text-slate-500">Aprobación de Calidad:</span>
                                                    <strong className="text-slate-800 dark:text-white">{compareMetrics.approvalRate.toFixed(1)}%</strong>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="py-10 text-center text-slate-400 italic text-xs">
                                        Selecciona otra obra del listado para proyectar la comparación de rendimiento operacional.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* SIMULACIONES */}
                    {activeSection === 'simulations' && (
                        <div className="p-6 md:p-8 space-y-6 max-w-4xl w-full mx-auto animate-fade-in">
                            <div className="bg-white dark:bg-[#0f1524] p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
                                <h3 className="text-sm font-bold text-slate-850 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-indigo-500">science</span>
                                    Simulador de Atrasos en Carta Gantt (Efecto Sucesor)
                                </h3>
                                <p className="text-xs text-slate-500">
                                    Selecciona una tarea de la Carta Gantt real de la obra y simula cuántos días de retraso tendría el proyecto en base a las dependencias (`predecesors`) programadas.
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 text-xs">
                                    <div>
                                        <label className="block text-slate-450 font-bold uppercase tracking-wider mb-2">Seleccionar Tarea</label>
                                        <select
                                            value={selectedSimTaskId}
                                            onChange={(e) => setSelectedSimTaskId(e.target.value)}
                                            className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl font-bold text-slate-800 dark:text-white"
                                        >
                                            <option value="">-- Seleccionar tarea del proyecto --</option>
                                            {localTasks.filter(t => t.type === 'task').map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-slate-450 font-bold uppercase tracking-wider mb-2">Días de Atraso Simulados</label>
                                        <input
                                            type="number"
                                            value={simDelayDays}
                                            onChange={(e) => setSimDelayDays(Number(e.target.value))}
                                            className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl font-bold text-slate-800 dark:text-white"
                                            min="1"
                                            max="100"
                                        />
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <button
                                        onClick={handleRunSimulation}
                                        disabled={!selectedSimTaskId}
                                        className="px-4 py-2.5 bg-primary disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-1.5"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">play_arrow</span>
                                        Calcular Desviación Simulada
                                    </button>
                                </div>

                                {simResult && (
                                    <div className="bg-indigo-950/20 dark:bg-slate-900/60 p-5 rounded-2xl border border-indigo-500/20 text-xs space-y-4 pt-4">
                                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest block">Resultado de la Simulación</span>
                                        
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                            <div className="bg-white dark:bg-[#0f1524] p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                                                <span className="text-[9px] text-slate-400 uppercase tracking-wide">Término Original</span>
                                                <p className="font-extrabold text-slate-800 dark:text-white mt-1">{simResult.originalProjectEnd}</p>
                                            </div>
                                            <div className="bg-white dark:bg-[#0f1524] p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                                                <span className="text-[9px] text-slate-400 uppercase tracking-wide">Término Simulado</span>
                                                <p className="font-extrabold text-indigo-600 dark:text-indigo-400 mt-1">{simResult.simulatedProjectEnd}</p>
                                            </div>
                                            <div className="bg-white dark:bg-[#0f1524] p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                                                <span className="text-[9px] text-slate-400 uppercase tracking-wide">Desviación Total</span>
                                                <p className="font-extrabold text-red-500 mt-1">+{simResult.diffDays} días</p>
                                            </div>
                                            <div className="bg-white dark:bg-[#0f1524] p-3 rounded-xl border border-slate-100 dark:border-slate-800">
                                                <span className="text-[9px] text-slate-400 uppercase tracking-wide">Tareas Afectadas</span>
                                                <p className="font-extrabold text-slate-800 dark:text-white mt-1">{simResult.affectedCount} partidas</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* CONFIGURACION */}
                    {activeSection === 'config' && (
                        <div className="p-6 md:p-8 space-y-8 max-w-5xl w-full mx-auto animate-fade-in">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div>
                                    <h3 className="text-base font-black text-slate-850 dark:text-white flex items-center gap-2">
                                        <span className="material-symbols-outlined text-primary text-[24px]">settings</span>
                                        Panel de Configuración del Centro de Inteligencia
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Define las metas, tolerancias y modelos de análisis que gobiernan el cálculo de alertas y recomendaciones en tiempo real.
                                    </p>
                                </div>
                                <button
                                    onClick={handleSaveConfig}
                                    className="px-5 py-3 bg-gradient-to-r from-indigo-650 to-indigo-600 hover:from-indigo-600 hover:to-indigo-550 text-white rounded-2xl text-xs font-black transition-all shadow-md hover:scale-[1.02] flex items-center gap-2"
                                >
                                    <span className="material-symbols-outlined text-[16px]">save</span>
                                    Guardar Configuración
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* CARD 1: UMBRALES DE ALERTAS */}
                                <div className="bg-white dark:bg-[#0f1524] p-6 rounded-3xl border border-slate-200 dark:border-slate-800/80 shadow-sm space-y-6">
                                    <h4 className="text-xs font-black text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[18px]">notifications_active</span>
                                        Umbrales y Tolerancias de Obra
                                    </h4>

                                    <div className="space-y-4 text-xs">
                                        <div>
                                            <label className="block text-slate-500 font-bold uppercase tracking-wider mb-1.5">Meta de Cumplimiento PPC (%)</label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min="50"
                                                    max="95"
                                                    step="5"
                                                    value={formSettings.targetPpc}
                                                    onChange={(e) => setFormSettings({ ...formSettings, targetPpc: Number(e.target.value) })}
                                                    className="flex-1 accent-indigo-600"
                                                />
                                                <span className="w-12 text-center font-bold text-slate-800 dark:text-white bg-slate-550/10 dark:bg-slate-850 px-2 py-1 rounded-lg">
                                                    {formSettings.targetPpc}%
                                                </span>
                                            </div>
                                            <span className="text-[10px] text-slate-400 mt-1 block">Define el estándar óptimo de tareas ejecutadas en terreno para gatillar alertas en recomendaciones.</span>
                                        </div>

                                        <div>
                                            <label className="block text-slate-500 font-bold uppercase tracking-wider mb-1.5">Tolerancia de Calidad (% Aprobación)</label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="range"
                                                    min="60"
                                                    max="100"
                                                    step="5"
                                                    value={formSettings.qualityTolerance}
                                                    onChange={(e) => setFormSettings({ ...formSettings, qualityTolerance: Number(e.target.value) })}
                                                    className="flex-1 accent-indigo-650"
                                                />
                                                <span className="w-12 text-center font-bold text-slate-800 dark:text-white bg-slate-550/10 dark:bg-slate-850 px-2 py-1 rounded-lg">
                                                    {formSettings.qualityTolerance}%
                                                </span>
                                            </div>
                                            <span className="text-[10px] text-slate-400 mt-1 block">Porcentaje mínimo de protocolos aprobados para considerar el módulo de calidad en estado óptimo.</span>
                                        </div>

                                        <div>
                                            <label className="block text-slate-500 font-bold uppercase tracking-wider mb-1.5">Umbral de Atraso en Carta Gantt (días)</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="30"
                                                    value={formSettings.ganttAlertThreshold}
                                                    onChange={(e) => setFormSettings({ ...formSettings, ganttAlertThreshold: Math.max(1, Number(e.target.value)) })}
                                                    className="w-20 p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl font-bold text-slate-800 dark:text-white text-center"
                                                />
                                                <span className="text-slate-500 font-medium">días hábiles de desviación</span>
                                            </div>
                                            <span className="text-[10px] text-slate-400 mt-1 block">Una tarea se clasificará como "atrasada" si supera esta desviación respecto a su fecha de término planificada.</span>
                                        </div>
                                    </div>
                                </div>

                                {/* CARD 2: MODELOS Y RANGO DE VENTANAS */}
                                <div className="bg-white dark:bg-[#0f1524] p-6 rounded-3xl border border-slate-200 dark:border-slate-800/80 shadow-sm space-y-6">
                                    <h4 className="text-xs font-black text-indigo-500 uppercase tracking-widest flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[18px]">analytics</span>
                                        Modelos y Parámetros Analíticos
                                    </h4>

                                    <div className="space-y-4 text-xs">
                                        <div>
                                            <label className="block text-slate-500 font-bold uppercase tracking-wider mb-1.5">Ventana de Análisis Lookahead (semanas)</label>
                                            <select
                                                value={formSettings.lookaheadWindowWeeks}
                                                onChange={(e) => setFormSettings({ ...formSettings, lookaheadWindowWeeks: Number(e.target.value) })}
                                                className="w-full p-3 bg-slate-50 dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl font-bold text-slate-800 dark:text-white"
                                            >
                                                <option value="2">2 Semanas adelante</option>
                                                <option value="3">3 Semanas adelante (Recomendado)</option>
                                                <option value="4">4 Semanas adelante</option>
                                                <option value="6">6 Semanas adelante</option>
                                            </select>
                                            <span className="text-[10px] text-slate-400 mt-1 block">Rango de tiempo en el que se buscan restricciones de lookahead sin liberar.</span>
                                        </div>

                                        <div>
                                            <label className="block text-slate-500 font-bold uppercase tracking-wider mb-1.5">Excluir Hitos del Diagnóstico</label>
                                            <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-150 dark:border-slate-800/50 mt-1">
                                                <span className="text-[11px] text-slate-550 dark:text-slate-400 font-medium">Ignorar hitos y tareas de duración cero</span>
                                                <button
                                                    onClick={() => setFormSettings({ ...formSettings, excludeMilestones: !formSettings.excludeMilestones })}
                                                    className={`w-12 h-6 rounded-full transition-colors flex items-center p-0.5 ${
                                                        formSettings.excludeMilestones ? 'bg-primary justify-end' : 'bg-slate-300 dark:bg-slate-850 justify-start'
                                                    }`}
                                                >
                                                    <span className="w-5 h-5 rounded-full bg-white shadow-sm block"></span>
                                                </button>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-slate-500 font-bold uppercase tracking-wider mb-1.5">Modelo del Motor de Simulación</label>
                                            <div className="grid grid-cols-2 gap-3 mt-1">
                                                <button
                                                    onClick={() => setFormSettings({ ...formSettings, simulationModel: 'cpm' })}
                                                    className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition-all ${
                                                        formSettings.simulationModel === 'cpm'
                                                            ? 'border-primary bg-primary/5 dark:bg-primary/10 text-slate-850 dark:text-white shadow-sm'
                                                            : 'border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                                                    }`}
                                                >
                                                    <span className="font-extrabold text-[11px]">Ruta Crítica (CPM)</span>
                                                    <span className="text-[9px] text-slate-400 dark:text-slate-500">Propaga atrasos usando dependencias reales.</span>
                                                </button>
                                                <button
                                                    onClick={() => setFormSettings({ ...formSettings, simulationModel: 'simple' })}
                                                    className={`p-3 rounded-xl border text-left flex flex-col gap-1 transition-all ${
                                                        formSettings.simulationModel === 'simple'
                                                            ? 'border-primary bg-primary/5 dark:bg-primary/10 text-slate-850 dark:text-white shadow-sm'
                                                            : 'border-slate-200 dark:border-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-900/50'
                                                    }`}
                                                >
                                                    <span className="font-extrabold text-[11px]">Secuencial Directo</span>
                                                    <span className="text-[9px] text-slate-400 dark:text-slate-500">Desplaza de forma directa las fechas.</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
